import { useDebounce } from "@rothoblaas/design-system";
import { memo, useEffect, useMemo, useState, type FC } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import FormFieldsContainer from "../../../components/ui/FormFieldsContainer";
import useI18n from "../../../hooks/useTranslations";
import type { ConnectorFamilyForSettings } from "../../../http/deckDesigner/types/connectorFamilies";
import type { ConnectorForSettings } from "../../../http/deckDesigner/types/connectors";
import type { Settings } from "../../../http/deckDesigner/types/settings";
import type { SupportFamilyForSettings } from "../../../http/deckDesigner/types/supportFamilies";
import type { SupportForSettings } from "../../../http/deckDesigner/types/supports";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { keepOnlyForCalculationWhileSelected } from "../../../utils/offeredSettingsItems";
import CompatibilityModal, { type CompatibilityModalType } from "./CompatibilityModal";
import ConnectorFields from "./ConnectorFields";
import type { TerrainSlope } from "./deckOutline";
import {
  getSingleOptionAutoSelectSignature,
  useSingleOptionAutoSelect,
} from "../../../hooks/useSingleOptionAutoSelect";
import SupportFields from "./SupportFields";
import { useConnectorOptions } from "./useConnectorOptions";
import { useScrewCompatibilityError } from "./useScrewCompatibilityError";
import { useSuggestedScrew } from "./useSuggestedScrew";
import { useSupportOptions } from "./useSupportOptions";

type ConnectorsCardProps = {
  defaultValues: DeckDesignerConfigurationFormValues;
  settings: Settings | undefined;
  hasError: boolean | undefined;
  hasVisitedAutoSelectTriangleState: (key: string) => boolean;
  rememberAutoSelectTriangleState: (key: string) => void;
  resetAutoSelectTriangleHistory: () => void;
};

const ERROR_FIELD_NAMES = ["connectorSubstructureScrew", "connectorBoardScrew", "supportScrew"] as const;

const ConnectorsCard: FC<ConnectorsCardProps> = ({
  defaultValues,
  settings,
  hasError,
  hasVisitedAutoSelectTriangleState,
  rememberAutoSelectTriangleState,
  resetAutoSelectTriangleHistory,
}) => {
  const i18n = useI18n();
  const errorsTranslation = i18n.translation.deckDesignerModule.configurations.errors;

  const { control, getValues, setValue } = useFormContext<DeckDesignerConfigurationFormValues>();
  // Scoped to this card's own fields: reading formState from the context subscribes the whole
  // form (useForm) instead, re-rendering every card on any validation.
  const { errors } = useFormState({ control, name: ERROR_FIELD_NAMES });

  const fasteningType = useWatch({ control, name: "fasteningType", exact: true });
  const groovingType = useWatch({ control, name: "groovingType", exact: true });
  const gap = useWatch({ control, name: "gap", exact: true });
  const connectorFamily = useWatch({ control, name: "connectorFamily", exact: true });
  const connector = useWatch({ control, name: "connector", exact: true });
  const connectorSubstructureScrew = useWatch({ control, name: "connectorSubstructureScrew", exact: true });
  const connectorBoardScrew = useWatch({ control, name: "connectorBoardScrew", exact: true });
  const substructureMaterial = useWatch({ control, name: "substructureMaterial", exact: true });
  const substructureMaterialClass = useWatch({ control, name: "substructureMaterialClass", exact: true });
  const boardThickness = useWatch({ control, name: "boardThickness", exact: true });
  const boardWidth = useWatch({ control, name: "boardWidth", exact: true });
  const groovingHeight = useWatch({ control, name: "groovingHeight", exact: true });
  const substructure = useWatch({ control, name: "substructure", exact: true });
  const secondaryItem = useWatch({ control, name: "secondaryItem", exact: true });
  const supportFamily = useWatch({ control, name: "supportFamily", exact: true });
  const support = useWatch({ control, name: "support", exact: true });
  const supportScrew = useWatch({ control, name: "supportScrew", exact: true });
  // Debounced: totalHeight commits on every keystroke (see ShapeAndSizeCard), so a raw watch would
  // run the height filtering below - and clear a still-valid connector/support - against transient,
  // mid-typing values (an empty field, or "1" on the way to "120") instead of the settled one.
  const totalHeight = useDebounce(useWatch({ control, name: "totalHeight", exact: true }), 500);
  const shape = useWatch({ control, name: "shape", exact: true });
  const base = useDebounce(useWatch({ control, name: "base", exact: true }), 500);
  const height = useDebounce(useWatch({ control, name: "height", exact: true }), 500);
  const secondaryBase = useDebounce(useWatch({ control, name: "secondaryBase", exact: true }), 500);
  const secondaryHeight = useDebounce(useWatch({ control, name: "secondaryHeight", exact: true }), 500);
  const lShapeCutoutCorner = useWatch({ control, name: "lShapeCutoutCorner", exact: true });
  const footprint = useMemo(
    () => ({ shape, base, height, secondaryBase, secondaryHeight, lShapeCutoutCorner }),
    [base, height, lShapeCutoutCorner, shape, secondaryBase, secondaryHeight],
  );
  const liveLoad = useWatch({ control, name: "liveLoad", exact: true });
  const deflection = useWatch({ control, name: "deflection", exact: true });
  const hasHorizontalBoards = useWatch({ control, name: "hasHorizontalBoards", exact: true });
  const joistsSpacing = useWatch({ control, name: "joistsSpacing", exact: true });
  const unitSystem = useWatch({ control, name: "unitSystem", exact: true });
  const supportSpacingInput = useMemo(
    () => ({
      liveLoad,
      deflection,
      shape,
      base,
      height,
      secondaryBase,
      secondaryHeight,
      hasHorizontalBoards,
      substructureMaterialClass,
      substructure,
      joistsSpacing,
    }),
    [
      base,
      deflection,
      hasHorizontalBoards,
      height,
      joistsSpacing,
      liveLoad,
      secondaryBase,
      secondaryHeight,
      shape,
      substructure,
      substructureMaterialClass,
    ],
  );
  const slopeDegrees = useDebounce(useWatch({ control, name: "slopeDegrees", exact: true }), 500);
  const slopeArrow = useWatch({ control, name: "slopeArrow", exact: true });
  const terrainSlope = useMemo((): TerrainSlope => ({ slopeDegrees, slopeArrow }), [slopeDegrees, slopeArrow]);

  // What the fields below may offer: items sent only because the calculation uses them stay only while
  // the form still holds them (see keepOnlyForCalculationWhileSelected). The settings themselves when
  // nothing is flagged.
  const connectorFamilyCode = connectorFamily?.code;
  const connectorConnectorFamilyCode = connector?.connectorFamily.code;
  const connectorItemId = connector?.item.id;
  const supportItemId = support?.item.id;
  const connectorSubstructureScrewItemId = connectorSubstructureScrew?.item.id;
  const connectorBoardScrewItemId = connectorBoardScrew?.item.id;
  const supportScrewItemId = supportScrew?.item.id;
  const offeredSettings = useMemo(() => {
    if (!settings) {
      return settings;
    }

    const selectedScrewIds = new Set([connectorSubstructureScrewItemId, connectorBoardScrewItemId, supportScrewItemId]);
    const connectorFamilies = keepOnlyForCalculationWhileSelected(
      settings.connectorFamilies,
      (option) => option.code === connectorFamilyCode || option.code === connectorConnectorFamilyCode,
    );
    const connectors = keepOnlyForCalculationWhileSelected(
      settings.connectors,
      (option) => option.item.id === connectorItemId,
    );
    const screws = keepOnlyForCalculationWhileSelected(settings.screws, (option) =>
      selectedScrewIds.has(option.item.id),
    );
    const supports = keepOnlyForCalculationWhileSelected(
      settings.supports,
      (option) => option.item.id === supportItemId,
    );
    // A dropped family takes its mappings along: they embed it, and would show it again (e.g. by name in
    // the compatibility table) - as the server already does for the families it doesn't send.
    const connectorMappings =
      connectorFamilies === settings.connectorFamilies
        ? settings.connectorMappings
        : (() => {
            const offeredFamilyCodes = new Set(connectorFamilies.map((option) => option.code));
            return settings.connectorMappings.filter((mapping) => offeredFamilyCodes.has(mapping.connectorFamily.code));
          })();

    return connectorFamilies === settings.connectorFamilies &&
      connectors === settings.connectors &&
      screws === settings.screws &&
      supports === settings.supports
      ? settings
      : { ...settings, connectorFamilies, connectorMappings, connectors, screws, supports };
  }, [
    connectorBoardScrewItemId,
    connectorConnectorFamilyCode,
    connectorFamilyCode,
    connectorItemId,
    connectorSubstructureScrewItemId,
    settings,
    supportItemId,
    supportScrewItemId,
  ]);

  const { rememberSingleOptionChange, shouldAutoSelectSingleOption } = useSingleOptionAutoSelect();
  const [compatibilityModal, setCompatibilityModal] = useState<CompatibilityModalType | null>(null);

  const connectorOptions = useConnectorOptions({
    settings: offeredSettings,
    fasteningType,
    groovingType,
    gap,
    connectorFamily,
    connector,
    substructureMaterial,
    boardThickness,
    boardWidth,
    groovingHeight,
    substructure,
    secondaryItem,
    support,
    totalHeight,
  });
  const {
    connectorFamilyByCode,
    connectorMappingsForBoardContext,
    mappedSecondaryItem,
    filteredConnectorFamilies,
    selectedConnectorFamily,
    selectedConnectorFamilyIsAvailable,
    selectedConnectorFamilyFitsBoardConstraints,
    maxScrewConnectorLength,
    filteredConnectors,
    filteredConnectorById,
    firstSelectableScrewConnector,
    singleSelectableConnector,
    shouldShowConnectorScrew,
    shouldShowConnectorBoardScrew,
    filteredConnectorScrews,
    filteredConnectorBoardScrews,
    hasNoCompatibleConnectorSubstructureScrew,
    hasNoCompatibleConnectorBoardScrew,
  } = connectorOptions;
  const selectedConnectorFamilyCode = selectedConnectorFamily?.code;

  const supportOptions = useSupportOptions({
    settings: offeredSettings,
    supportFamily,
    support,
    substructureMaterial,
    substructureMaterialClass,
    substructure,
    boardThickness,
    secondaryItem,
    totalHeight,
    footprint,
    terrainSlope,
    supportSpacingInput,
    unitSystem,
    connectorMappingsForSelectedConfiguration: connectorOptions.connectorMappingsForSelectedConfiguration,
    supportCheckConnectors: connectorOptions.supportCheckConnectors,
    isCompatibilityModalOpen: compatibilityModal === "support",
    isSpacingCompatibilityModalOpen: compatibilityModal === "spacing",
  });
  const {
    supportFamilyByCode,
    filteredSupportFamilies,
    validSupportFamilyCodes,
    filteredSupports,
    filteredSupportById,
    shouldShowSupportScrew,
    filteredSupportScrews,
    hasNoCompatibleSupportScrew,
  } = supportOptions;

  const connectorFamilyAutoSelectSignature = useMemo(
    () =>
      getSingleOptionAutoSelectSignature(
        filteredConnectorFamilies,
        (option) => option.code,
        connectorMappingsForBoardContext?.map((option) => option.id).join("|"),
      ),
    [connectorMappingsForBoardContext, filteredConnectorFamilies],
  );

  const connectorAutoSelectSignature = useMemo(
    () =>
      getSingleOptionAutoSelectSignature(
        filteredConnectors,
        (option) => option.item.id,
        [
          boardThickness,
          secondaryItem?.item.id,
          selectedConnectorFamilyCode,
          substructure?.id,
          support?.item.id,
          totalHeight,
        ].join("|"),
      ),
    [
      boardThickness,
      filteredConnectors,
      secondaryItem?.item.id,
      selectedConnectorFamilyCode,
      substructure?.id,
      support?.item.id,
      totalHeight,
    ],
  );

  const supportFamilyAutoSelectSignature = useMemo(
    () =>
      getSingleOptionAutoSelectSignature(
        filteredSupportFamilies,
        (option) => option.code,
        [boardThickness, secondaryItem?.item.id, substructure?.id, substructureMaterialClass?.id, totalHeight].join(
          "|",
        ),
      ),
    [
      boardThickness,
      filteredSupportFamilies,
      secondaryItem?.item.id,
      substructure?.id,
      substructureMaterialClass?.id,
      totalHeight,
    ],
  );

  const supportAutoSelectSignature = useMemo(
    () =>
      getSingleOptionAutoSelectSignature(
        filteredSupports,
        (option) => option.item.id,
        [
          boardThickness,
          secondaryItem?.item.id,
          substructure?.id,
          substructureMaterialClass?.id,
          supportFamily?.code,
          totalHeight,
        ].join("|"),
      ),
    [
      boardThickness,
      secondaryItem?.item.id,
      substructure?.id,
      substructureMaterialClass?.id,
      supportFamily?.code,
      totalHeight,
      filteredSupports,
    ],
  );

  useEffect(() => {
    if (!secondaryItem || mappedSecondaryItem === undefined) {
      return;
    }

    if (mappedSecondaryItem?.item.id !== secondaryItem.item.id) {
      setValue("secondaryItem", null);
      setValue("support", null);
    }
  }, [mappedSecondaryItem, secondaryItem, setValue]);

  useEffect(() => {
    const singleConnectorFamily = filteredConnectorFamilies?.length === 1 ? filteredConnectorFamilies[0] : undefined;

    if (
      singleConnectorFamily &&
      connectorFamily?.code !== singleConnectorFamily.code &&
      shouldAutoSelectSingleOption("connectorFamily", connectorFamilyAutoSelectSignature)
    ) {
      setValue("connectorFamily", singleConnectorFamily);
    }
  }, [
    connectorFamily?.code,
    connectorFamilyAutoSelectSignature,
    filteredConnectorFamilies,
    shouldAutoSelectSingleOption,
    setValue,
  ]);

  // The one place deciding the connector: keep it while it's still offered, otherwise auto-pick the
  // only one offered (for a screw family, the first offered screw connector), otherwise clear it - so
  // the rules can't undo each other's write within one commit. The screws follow on their own (see
  // useSuggestedScrew).
  useEffect(() => {
    // filteredConnectorFamilies additionally excludes families that no longer fit the board or the
    // total height (see selectedConnectorFamilyFitsBoardConstraints) - now that the family field
    // explains why nothing is selectable, it's safe (and clearer) to clear the stale selection
    // instead of leaving it selected alongside an unrelated board error.
    if (
      connectorFamily &&
      connectorMappingsForBoardContext &&
      !(selectedConnectorFamilyIsAvailable && selectedConnectorFamilyFitsBoardConstraints)
    ) {
      setValue("connectorFamily", null);
      setValue("connector", null);
      return;
    }

    if (!filteredConnectors) {
      return;
    }

    // Checked against the height-aware filteredConnectors - totalHeight is debounced upstream, so
    // this only reacts once the value has actually settled. A screw family's connector must be a
    // screw connector too.
    const isScrewConnectorFamily = selectedConnectorFamily?.isScrew === true && maxScrewConnectorLength != null;
    const offeredConnector = connector ? filteredConnectorById.get(connector.item.id) : undefined;

    if (
      offeredConnector &&
      (!isScrewConnectorFamily || (offeredConnector.connectorFamily.isScrew === true && offeredConnector.screw != null))
    ) {
      return;
    }

    const nextConnector = isScrewConnectorFamily ? firstSelectableScrewConnector : singleSelectableConnector;

    if (nextConnector && shouldAutoSelectSingleOption("connector", connectorAutoSelectSignature)) {
      const triangleKey = `connector:${nextConnector.item.id}::${support?.item.id ?? ""}::${substructure?.id ?? ""}`;

      if (!hasVisitedAutoSelectTriangleState(triangleKey)) {
        rememberAutoSelectTriangleState(triangleKey);
        // Like the other single-option auto-selects, picking the only option leaves the field's dirty
        // state alone; picking the first of several screw connectors marks it dirty.
        setValue(
          "connector",
          nextConnector,
          nextConnector === singleSelectableConnector ? undefined : { shouldDirty: true },
        );

        if (!connectorFamily && shouldAutoSelectSingleOption("connectorFamily", connectorFamilyAutoSelectSignature)) {
          setValue(
            "connectorFamily",
            connectorFamilyByCode.get(nextConnector.connectorFamily.code) ?? nextConnector.connectorFamily,
          );
        }

        return;
      }
    }

    if (!connector) {
      return;
    }

    // A screw family without any screw connector left always clears it; otherwise only a connector
    // that's no longer offered at all is cleared.
    if (isScrewConnectorFamily && !nextConnector) {
      setValue("connector", null, { shouldDirty: true });
    } else if (!offeredConnector) {
      setValue("connector", null);
    }
  }, [
    connector,
    connectorAutoSelectSignature,
    connectorFamily,
    connectorFamilyAutoSelectSignature,
    connectorFamilyByCode,
    connectorMappingsForBoardContext,
    filteredConnectorById,
    filteredConnectors,
    firstSelectableScrewConnector,
    hasVisitedAutoSelectTriangleState,
    maxScrewConnectorLength,
    rememberAutoSelectTriangleState,
    selectedConnectorFamily?.isScrew,
    selectedConnectorFamilyFitsBoardConstraints,
    selectedConnectorFamilyIsAvailable,
    shouldAutoSelectSingleOption,
    singleSelectableConnector,
    substructure?.id,
    support?.item.id,
    setValue,
  ]);

  useEffect(() => {
    const singleSupportFamily = filteredSupportFamilies?.length === 1 ? filteredSupportFamilies[0] : undefined;

    if (
      singleSupportFamily &&
      supportFamily?.code !== singleSupportFamily.code &&
      shouldAutoSelectSingleOption("supportFamily", supportFamilyAutoSelectSignature)
    ) {
      setValue("supportFamily", singleSupportFamily);
    }
  }, [
    filteredSupportFamilies,
    shouldAutoSelectSingleOption,
    setValue,
    supportFamily?.code,
    supportFamilyAutoSelectSignature,
  ]);

  // One decision per pass for the support: keep it while still offered, else auto-pick the single offered
  // one, else clear it. Two separate effects (pick, then clear) read the same stale support in one pass:
  // the pick was overwritten by the clear, and the triangle history then refused to pick it again.
  useEffect(() => {
    if (!filteredSupports || (support && filteredSupportById.has(support.item.id))) {
      return;
    }

    const singleVisibleSupport = filteredSupports.length === 1 ? filteredSupports[0] : undefined;

    if (singleVisibleSupport && shouldAutoSelectSingleOption("support", supportAutoSelectSignature)) {
      const triangleKey = `support:${singleVisibleSupport.item.id}::${connector?.item.id ?? ""}::${substructure?.id ?? ""}`;

      if (!hasVisitedAutoSelectTriangleState(triangleKey)) {
        rememberAutoSelectTriangleState(triangleKey);
        setValue("support", singleVisibleSupport);

        if (!supportFamily && shouldAutoSelectSingleOption("supportFamily", supportFamilyAutoSelectSignature)) {
          setValue(
            "supportFamily",
            supportFamilyByCode.get(singleVisibleSupport.supportFamily.code) ?? singleVisibleSupport.supportFamily,
          );
        }

        return;
      }
    }

    if (support) {
      setValue("support", null);
    }
  }, [
    connector?.item.id,
    filteredSupportById,
    filteredSupports,
    hasVisitedAutoSelectTriangleState,
    rememberAutoSelectTriangleState,
    shouldAutoSelectSingleOption,
    setValue,
    substructure?.id,
    support,
    supportAutoSelectSignature,
    supportFamily,
    supportFamilyAutoSelectSignature,
    supportFamilyByCode,
  ]);

  useSuggestedScrew(
    "connectorSubstructureScrew",
    connectorSubstructureScrew,
    filteredConnectorScrews,
    shouldShowConnectorScrew && !!connector,
    true,
  );
  useSuggestedScrew(
    "connectorBoardScrew",
    connectorBoardScrew,
    filteredConnectorBoardScrews,
    shouldShowConnectorBoardScrew,
    true,
  );
  useSuggestedScrew("supportScrew", supportScrew, filteredSupportScrews, shouldShowSupportScrew);

  useScrewCompatibilityError(
    "connectorSubstructureScrew",
    hasNoCompatibleConnectorSubstructureScrew,
    errorsTranslation.noCompatibleConnectorSubstructureScrew,
    errors.connectorSubstructureScrew,
  );
  useScrewCompatibilityError(
    "supportScrew",
    hasNoCompatibleSupportScrew,
    errorsTranslation.noCompatibleSupportScrew,
    errors.supportScrew,
  );
  useScrewCompatibilityError(
    "connectorBoardScrew",
    hasNoCompatibleConnectorBoardScrew,
    errorsTranslation.noCompatibleConnectorBoardScrew,
    errors.connectorBoardScrew,
  );

  // Checked against the height-aware filteredSupportFamilies (not just material class) so a family
  // that's become incompatible with the current total height also gets cleared - totalHeight is
  // debounced upstream, so this only reacts once the value has actually settled. The family codes of
  // filteredSupportFamilies are validSupportFamilyCodes.
  useEffect(() => {
    if (!supportFamily || !filteredSupportFamilies) {
      return;
    }

    if (!validSupportFamilyCodes.has(supportFamily.code)) {
      setValue("supportFamily", null);
      setValue("support", null);
    }
  }, [filteredSupportFamilies, setValue, supportFamily, validSupportFamilyCodes]);

  // Manual picks, after the field's own change: a new auto-select cascade starts, and a manual clear
  // is remembered so the single-option auto-select doesn't immediately undo it.
  const handleConnectorFamilyChange = (option: ConnectorFamilyForSettings) => {
    resetAutoSelectTriangleHistory();
    rememberSingleOptionChange("connectorFamily", connectorFamilyAutoSelectSignature, option);
  };

  const handleConnectorChange = (nextConnector: ConnectorForSettings | null) => {
    resetAutoSelectTriangleHistory();
    rememberSingleOptionChange("connector", connectorAutoSelectSignature, nextConnector);

    if (!connectorFamily) {
      const nextConnectorFamily = nextConnector
        ? (connectorFamilyByCode.get(nextConnector.connectorFamily.code) ?? nextConnector.connectorFamily)
        : null;

      rememberSingleOptionChange("connectorFamily", connectorFamilyAutoSelectSignature, nextConnectorFamily);
      setValue("connectorFamily", nextConnectorFamily);
    }
  };

  const handleSupportFamilyChange = (option: SupportFamilyForSettings) => {
    const currentSupport = getValues("support");

    resetAutoSelectTriangleHistory();
    rememberSingleOptionChange("supportFamily", supportFamilyAutoSelectSignature, option);

    if (currentSupport && currentSupport.supportFamily.code !== option.code) {
      setValue("support", null);
    }
  };

  const handleSupportChange = (nextSupport: SupportForSettings | null) => {
    const nextSupportFamily = nextSupport
      ? (supportFamilyByCode.get(nextSupport.supportFamily.code) ?? nextSupport.supportFamily)
      : null;

    resetAutoSelectTriangleHistory();
    rememberSingleOptionChange("support", supportAutoSelectSignature, nextSupport);

    if (nextSupportFamily && supportFamily?.code !== nextSupportFamily.code) {
      rememberSingleOptionChange("supportFamily", supportFamilyAutoSelectSignature, nextSupportFamily);
      setValue("supportFamily", nextSupportFamily);
    }
  };

  return (
    <div>
      <FormFieldsContainer fullWidth>
        <ConnectorFields
          defaultValues={defaultValues}
          settings={offeredSettings}
          hasError={hasError}
          connectorOptions={connectorOptions}
          connectorMaterial={connector?.connectorMaterial ?? null}
          onConnectorFamilyChange={handleConnectorFamilyChange}
          onConnectorChange={handleConnectorChange}
          onShowCompatibleValues={() => setCompatibilityModal("connector")}
        />
        <SupportFields
          defaultValues={defaultValues}
          settings={offeredSettings}
          hasError={hasError}
          supportOptions={supportOptions}
          onSupportFamilyChange={handleSupportFamilyChange}
          onSupportChange={handleSupportChange}
          onShowCompatibleValues={() =>
            setCompatibilityModal(supportOptions.hasNoSupportFamilyFittingSpacing ? "spacing" : "support")
          }
        />
        {compatibilityModal && (
          <CompatibilityModal
            type={compatibilityModal}
            settings={offeredSettings}
            fasteningTypeCode={fasteningType?.code}
            groovingTypeCode={groovingType?.code}
            isSymmetricalGrooving={connectorOptions.isSymmetricalGrooving}
            isAsymmetricalGrooving={connectorOptions.isAsymmetricalGrooving}
            substructure={substructure}
            secondaryItem={secondaryItem}
            boardThickness={boardThickness}
            totalHeight={totalHeight}
            slopeDegrees={terrainSlope.slopeDegrees}
            joistsSpacing={joistsSpacing}
            deflection={deflection}
            liveLoad={liveLoad}
            supportCompatibilityRows={supportOptions.supportCompatibilityRows}
            supportSpacingRows={supportOptions.supportSpacingRows}
            onClose={() => setCompatibilityModal(null)}
          />
        )}
      </FormFieldsContainer>
    </div>
  );
};

// Memoized: its props are stable, so it re-renders through its own watched fields and props only, not
// whenever its parent does.
export default memo(ConnectorsCard);
